import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight, MessageSquareText, BookOpen } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error | expired
  const [info, setInfo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!sessionId) {
      setState("error");
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setInfo(data);
          await refreshUser().catch(() => {});
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setInfo({ error: formatApiError(e.response?.data?.detail) || e.message });
          setState("error");
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  const isAi = info?.kind === "ai_extension";

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-3xl glass-strong p-8 sm:p-10 text-center"
          data-testid="payment-success-page"
        >
          {state === "checking" && (
            <div data-testid="payment-checking">
              <div className="relative mx-auto h-16 w-16 rounded-2xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-6">
                <Loader2 className="h-8 w-8 text-gold animate-spin" />
              </div>
              <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Zahlung wird geprüft…</h1>
              <p className="text-foreground/70 mt-3">Einen Moment bitte – wir bestätigen Ihre Zahlung und schalten Ihren Zugang frei.</p>
            </div>
          )}

          {state === "success" && (
            <div data-testid="payment-success">
              <div className="relative mx-auto h-16 w-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-6">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                <div className="absolute inset-0 blur-xl bg-emerald-500/20 -z-10 rounded-2xl" />
              </div>
              <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Vielen Dank für Ihren Kauf!</h1>
              <p className="text-foreground/70 mt-3 max-w-md mx-auto leading-relaxed">
                {isAi
                  ? "Ihr KI-Coach wurde verlängert und steht Ihnen ab sofort wieder uneingeschränkt zur Verfügung."
                  : "Ihr Vollzugang ist aktiv – alle 10 Module und der KI-Coach stehen Ihnen ab sofort zur Verfügung. Eine Bestätigung wurde an Ihre E-Mail gesendet."}
              </p>
              {info?.description && (
                <p className="mt-4 inline-block rounded-lg bg-gold/10 border border-gold/25 px-3 py-1.5 text-sm text-gold" data-testid="payment-description">
                  {info.description}
                </p>
              )}
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to={isAi ? "/coach" : "/dashboard"}
                  data-testid="payment-cta-primary"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300"
                >
                  {isAi ? <MessageSquareText className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
                  {isAi ? "Zum KI-Coach" : "Zum Kurs"}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {!isAi && (
                  <Link to="/coach" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full glass hover:glow-gold transition-all duration-300">
                    <MessageSquareText className="h-4 w-4 text-gold" /> KI-Coach öffnen
                  </Link>
                )}
              </div>
            </div>
          )}

          {(state === "timeout" || state === "error" || state === "expired") && (
            <div data-testid="payment-pending">
              <div className="relative mx-auto h-16 w-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-6">
                {state === "expired" ? <XCircle className="h-8 w-8 text-amber-400" /> : <Loader2 className="h-8 w-8 text-amber-400" />}
              </div>
              <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">
                {state === "expired" ? "Zahlung abgelaufen" : "Zahlung wird noch bearbeitet"}
              </h1>
              <p className="text-foreground/70 mt-3 max-w-md mx-auto leading-relaxed">
                {state === "expired"
                  ? "Die Zahlungssitzung ist abgelaufen. Bitte starten Sie den Kauf erneut."
                  : "Wir konnten den Zahlungsstatus noch nicht abschliessend bestätigen. Sollte der Betrag belastet worden sein, wird Ihr Zugang automatisch freigeschaltet – Sie erhalten zudem eine E-Mail-Bestätigung."}
              </p>
              <div className="mt-7">
                <Link
                  to="/dashboard"
                  data-testid="payment-back-dashboard"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full glass hover:glow-gold transition-all duration-300"
                >
                  Zurück zum Dashboard <ArrowRight className="h-4 w-4 text-gold" />
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
